import { memo, useCallback, type FC } from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { InclusionSection } from "@/components/filters/UserFilters/ProductsFilter/InclusionSection";
import { ExclusionSection } from "@/components/filters/UserFilters/ProductsFilter/ExclusionSection";
import { FILTER_MODE } from "@/components/filters/Condition/constants";
import type { FilterMode } from "@/components/filters/Condition/types";
import type { ProductFilterValue } from "@/components/filters/ProductFilter/types";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";
import type { ProductsSlice } from "./slice";

export const ProductsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("products");
  const { setSlice } = useCompanyFilterActions();

  const updateSection = useCallback(
    (index: number, patch: Partial<ProductsSlice["sections"][number]>) => {
      const sections = slice.sections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, ...patch } : section
      );
      setSlice("products", { ...slice, sections });
    },
    [slice, setSlice]
  );

  const handleInclusionProductSelect = useCallback(
    (sectionIndex: number, value: ProductFilterValue) => {
      updateSection(sectionIndex, { productIds: value.ids });
    },
    [updateSection]
  );

  const handleConditionChange = useCallback(
    (sectionIndex: number, condition: FilterMode) => {
      updateSection(sectionIndex, { inclusionMethod: condition });
    },
    [updateSection]
  );

  const handleFaiSelect = useCallback(
    (
      sectionIndex: number,
      selectedDepartmentIds: string[],
      selectedRoleIds: string[]
    ) => {
      updateSection(sectionIndex, {
        departmentIds: selectedDepartmentIds,
        roleIds: selectedRoleIds,
      });
    },
    [updateSection]
  );

  const handleInstallAgeChange = useCallback(
    (
      sectionIndex: number,
      installAge: { min?: number | null; max?: number | null }
    ) => {
      updateSection(sectionIndex, {
        installAgeMonths:
          installAge.min === undefined && installAge.max === undefined
            ? undefined
            : {
                min: installAge.min ?? undefined,
                max: installAge.max ?? undefined,
              },
      });
    },
    [updateSection]
  );

  const handleIntensityChange = useCallback(
    (
      sectionIndex: number,
      intensity: { min?: number | null; max?: number | null }
    ) => {
      updateSection(sectionIndex, {
        intensity:
          intensity.min === undefined && intensity.max === undefined
            ? undefined
            : {
                min: intensity.min ?? undefined,
                max: intensity.max ?? undefined,
              },
      });
    },
    [updateSection]
  );

  const handleExclusionProductSelect = useCallback(
    (value: ProductFilterValue) => {
      setSlice("products", {
        ...slice,
        excludedProductIds: value.ids,
      });
    },
    [setSlice, slice]
  );

  const handleAddSection = useCallback(() => {
    setSlice("products", {
      ...slice,
      sections: [
        ...slice.sections,
        {
          inclusionMethod: FILTER_MODE.ANY,
          productIds: [],
          departmentIds: [],
          roleIds: [],
          installAgeMonths: undefined,
          intensity: undefined,
        },
      ],
    });
  }, [setSlice, slice]);

  const handleRemoveSection = useCallback(
    (sectionIndex: number) => {
      if (slice.sections.length <= 1) return;
      const sections = slice.sections.filter((_, i) => i !== sectionIndex);
      setSlice("products", { ...slice, sections });
    },
    [setSlice, slice]
  );

  return (
    <div id="products-filter-container">
      {slice.sections.map((section, index) => (
        <InclusionSection
          key={`product-inclusion-${index}`}
          sectionId={`product-inclusion-${index}`}
          sectionIndex={index}
          condition={section.inclusionMethod}
          selectedProductIds={section.productIds}
          canRemove={slice.sections.length > 1}
          onConditionChange={handleConditionChange}
          onProductSelect={handleInclusionProductSelect}
          onRemoveSection={handleRemoveSection}
          selectedFaiIds={[...section.departmentIds, ...section.roleIds]}
          onFaiSelect={handleFaiSelect}
          installAgeMonths={section.installAgeMonths}
          onInstallAgeChange={handleInstallAgeChange}
          intensity={section.intensity}
          onIntensityChange={handleIntensityChange}
        />
      ))}

      <Button
        id="products-filter-add-section-btn"
        startIcon={<AddIcon />}
        onClick={handleAddSection}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        Add
      </Button>

      <ExclusionSection
        selectedProductIds={slice.excludedProductIds}
        onProductSelect={handleExclusionProductSelect}
      />
    </div>
  );
});

ProductsFilter.displayName = "ProductsFilter";
